import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtUSD } from "../../lib/api";
import { useBusiness } from "../../context/BusinessContext";
import { Printer, ArrowLeft, Mail } from "lucide-react";
import EmailInvoiceModal from "../../components/EmailInvoiceModal";

export default function InvoicePrint({ returnPath }) {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const { businesses } = useBusiness();
  const [inv, setInv] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/invoices/${invoiceId}`);
      setInv(data);
      if (data.customer_id) {
        try {
          const c = await api.get(`/customers/${data.customer_id}`);
          setCustomer(c.data);
        } catch { /* ignore */ }
      }
    })();
  }, [invoiceId]);

  if (!inv) return <div className="p-8 text-sm text-[#66645E]">Loading invoice…</div>;

  const biz = businesses.find((b) => b.id === inv.business_id) || {};

  return (
    <div className="bg-[#F5F4F0] min-h-screen">
      <div className="no-print flex items-center justify-between p-4 bg-white border-b border-[#1F1E1B] sticky top-0 z-10">
        <button onClick={() => navigate(returnPath || "..")} className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          {inv.last_emailed_at && (
            <span className="text-[11px] text-[#66645E] hidden sm:inline" data-testid="last-emailed-note">
              Last emailed {inv.last_emailed_at.slice(0, 10)} to {inv.last_emailed_to}
            </span>
          )}
          <button data-testid="email-invoice-btn" onClick={() => setEmailOpen(true)} className="bg-[#1A4135] text-white px-4 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#133128] flex items-center gap-2">
            <Mail className="w-4 h-4" /> Email
          </button>
          <button data-testid="print-btn" onClick={() => window.print()} className="bg-[#1F1E1B] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#2A2924] flex items-center gap-2">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="max-w-[8.5in] mx-auto bg-white border-2 border-[#1F1E1B] p-8 md:p-12 my-6 print-container" data-testid="invoice-print">
        <div className="flex items-start justify-between border-b-2 border-[#1F1E1B] pb-6 mb-8">
          <div>
            <div className="font-display text-3xl font-black tracking-tight">{biz.name}</div>
            {biz.address && <div className="text-sm text-[#66645E] mt-1 whitespace-pre-wrap">{biz.address}</div>}
            {biz.phone && <div className="text-sm text-[#66645E]">{biz.phone}</div>}
            {biz.email && <div className="text-sm text-[#66645E]">{biz.email}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#66645E]">Invoice</div>
            <div className="font-display text-2xl font-black tabular mt-1" data-testid="print-invoice-number">{inv.invoice_number}</div>
            <div className="text-xs text-[#66645E] mt-2">Issue · {inv.issue_date}</div>
            {inv.due_date && <div className="text-xs text-[#66645E]">Due · {inv.due_date}</div>}
            <div className="mt-2 inline-block text-[10px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 border border-[#1F1E1B]">{inv.status}</div>
          </div>
        </div>

        <div className="mb-8">
          <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#66645E] mb-1">Bill To</div>
          <div className="font-display text-xl font-bold">{inv.customer_name || customer?.name || "—"}</div>
          {customer?.address && <div className="text-sm text-[#66645E] whitespace-pre-wrap">{customer.address}</div>}
          {customer?.phone && <div className="text-sm text-[#66645E]">{customer.phone}</div>}
          {customer?.email && <div className="text-sm text-[#66645E]">{customer.email}</div>}
        </div>

        <table className="ledger-table mb-6">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th className="num">Qty</th>
              <th className="num">Rate</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((ln, i) => (
              <tr key={i}>
                <td className="text-xs uppercase text-[#66645E]">{ln.type}</td>
                <td>{ln.description}</td>
                <td className="num">{ln.quantity}</td>
                <td className="num">{fmtUSD(ln.rate)}</td>
                <td className="num font-bold">{fmtUSD(ln.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end">
          <div className="w-80">
            <div className="flex justify-between py-1 text-sm">
              <span>Subtotal</span><span className="tabular">{fmtUSD(inv.subtotal)}</span>
            </div>
            {inv.tax_amount > 0 && (
              <div className="flex justify-between py-1 text-sm">
                <span>Tax ({inv.tax_pct}%)</span><span className="tabular">{fmtUSD(inv.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between py-2 mt-2 border-t-2 border-[#1F1E1B]">
              <span className="font-bold uppercase tracking-wider text-sm">Total Due</span>
              <span className="font-display text-2xl font-black tabular text-[#1A4135]" data-testid="print-total">{fmtUSD(inv.total)}</span>
            </div>
            {inv.paid_at && (
              <div className="bg-[#1A4135] text-white text-center py-2 mt-3 font-bold uppercase tracking-[0.18em] text-xs">PAID · {inv.paid_at}</div>
            )}
          </div>
        </div>

        {inv.notes && (
          <div className="mt-8 pt-6 border-t border-[#D2CEC1]">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#66645E] mb-1">Notes</div>
            <div className="text-sm text-[#1F1E1B] whitespace-pre-wrap">{inv.notes}</div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-[#D2CEC1] text-[10px] text-[#66645E] tracking-wider text-center">
          Thank you for your business · {biz.name}
        </div>
      </div>

      {emailOpen && (
        <EmailInvoiceModal
          invoice={inv}
          customer={customer}
          onClose={() => setEmailOpen(false)}
          onSent={() => { api.get(`/invoices/${invoiceId}`).then((r) => setInv(r.data)); }}
        />
      )}
    </div>
  );
}
